import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-arrowheads';
import { lensMatchesPoint } from '../lensSections';
import { CARTO_KEY } from '../utils/cartoKey';

// Fix for Leaflet marker icon issue in React
// This is needed because Webpack handles assets differently than Leaflet expects
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Create a numbered icon for each marker. `isDimmed` = the point is filtered out
// by the Lens tag filter: it stays on the map for context but goes pale and
// stops taking clicks (the CSS kills pointer events on the whole icon).
const createNumberedIcon = (number, isSelected, isDimmed) => {
  return L.divIcon({
    className: `custom-div-icon${isDimmed ? ' dimmed' : ''}`,
    html: `
      <div class='marker-pin ${isSelected ? 'selected' : ''}${isDimmed ? ' dimmed' : ''}'>
        <span>${number}</span>
      </div>
    `,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -35]
  });
};

// This component creates the path between points with arrows
const ArrowPath = ({ segments }) => {
  const map = useMap();
  const pathsRef = useRef([]);
  
  useEffect(() => {
    // Remove any existing paths
    pathsRef.current.forEach(path => {
      if (path) path.removeFrom(map);
    });
    pathsRef.current = [];
    
    // Create individual segments from the segments prop
    if (segments && segments.length > 0) {
      segments.forEach(segmentInfo => {
        const path = L.polyline([segmentInfo.from, segmentInfo.to], segmentInfo.options)
        .arrowheads({
          size: '12px',
          frequency: 'endonly',
          fill: true,
          yawn: 40
        }).addTo(map);
        
        pathsRef.current.push(path);
      });
    }
    
    return () => {
      pathsRef.current.forEach(path => {
        if (path) path.removeFrom(map);
      });
    };
  }, [segments, map]);
  
  return null;
};

// A location is only placeable on the map once it has a real [lat, lng] pair.
// Imported route skeletons may not have coordinates yet, so every consumer of
// loc.coordinates must guard with this — otherwise Leaflet throws on an
// undefined LatLng and takes the whole editor down with it.
const isValidCoord = (c) =>
  Array.isArray(c) && c.length >= 2 &&
  Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1]));

// This is a component that handles map manipulation
const MapController = ({ locations }) => {
  const map = useMap();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initial setup - fit map to show all markers only once on initial render.
    // Fit only to points that actually have coordinates.
    if (!isInitialized) {
      const placed = locations.filter(loc => isValidCoord(loc.coordinates));
      if (placed.length > 0) {
        const bounds = L.latLngBounds(placed.map(loc => loc.coordinates));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
      setIsInitialized(true);
    }
  }, [locations, map, isInitialized]);

  return null;
};

// Component to handle marker movement
const DraggableMarker = ({ 
  position, 
  icon, 
  onMove, 
  isMoving, 
  locationId, 
  onLocationSelect,
  location,
  onStartMoving,
  isDimmed
}) => {
  const markerRef = useRef(null);
  const map = useMap();
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const newPosition = marker.getLatLng();
        onMove(locationId, [newPosition.lat, newPosition.lng]);
      }
    },
    click() {
      // Filtered-out points are inert; CSS already swallows the click, this is
      // the belt to that pair of braces.
      if (isDimmed) return;
      onLocationSelect(locationId);
    }
  };

  useEffect(() => {
    const marker = markerRef.current;
    if (marker) {
      if (isMoving === locationId) {
        marker.dragging.enable();
      } else {
        marker.dragging.disable();
      }
    }
  }, [isMoving, locationId]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      eventHandlers={eventHandlers}
      draggable={!isDimmed && isMoving === locationId}
      keyboard={!isDimmed}
    >
      <Popup>
        <div className="popup-content">
          <h3>{location.name}</h3>
          {isMoving === locationId ? (
            <p><strong>Click on map to place marker or drag it</strong></p>
          ) : (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onStartMoving(locationId);
              }}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Move
            </button>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

// Component to handle clicking on the map during move mode
const MapClickHandler = ({ isMoving, onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (isMoving) {
        onMapClick(isMoving, [e.latlng.lat, e.latlng.lng]);
      }
    }
  });
  return null;
};

const QuestMap = ({
  locations,
  selectedLocationId,
  onLocationSelect,
  onLocationMove,
  city,
  // View-only filter; its point part decides which pins stay live. See lensSections.js.
  lens = {},
  // uuid -> { tag: [recipient, ...] }, needed for the lens "assigned to" filter.
  tagAssigneesByLocation = {},
  currentUsername
}) => {
  const [isMoving, setIsMoving] = useState(null); // store the id of the location being moved

  // Determine if we should use Google Maps (default for Seoul) or CartoDB (others)
  // Logic: if city is 'seoul' -> Google Maps.
  // If city is 'all' or 'istanbul' -> CartoDB (or whatever was requested).
  // Fallback: check coordinates if city is not reliable?
  // Let's start with the explicit city prop.
  const useGoogleMaps = city === 'seoul';

  // Find center coordinates (use first location or default to Istanbul center)
  const defaultCenter = [41.0082, 28.9784]; // Istanbul center
  const firstPlaced = locations && locations.find(loc => isValidCoord(loc.coordinates));
  const initialCenter = firstPlaced ? firstPlaced.coordinates : defaultCenter;

  const pathSegments = [];

  // Create segments for the main path (not originating from branching points)
  if (locations.length > 1) {
    for (let i = 0; i < locations.length - 1; i++) {
      const startLoc = locations[i];
      const endLoc = locations[i + 1];

      if (startLoc.type !== 'pre-branching'
          && isValidCoord(startLoc.coordinates) && isValidCoord(endLoc.coordinates)) {
          pathSegments.push({
              from: startLoc.coordinates,
              to: endLoc.coordinates,
              options: {
                  color: '#3498db',
                  weight: 5,
                  opacity: 0.8
              }
          });
      }
    }
  }

  // Create segments for branches
  const branchingLocations = locations.filter(loc => loc.type === 'pre-branching' && loc.branches);
  
  branchingLocations.forEach(startLoc => {
    startLoc.branches.forEach(branch => {
        if (branch.next_location_uuid && branch.next_location_uuid !== 'END') {
            const endLoc = locations.find(loc => loc.uuid === branch.next_location_uuid);
            if (endLoc && isValidCoord(startLoc.coordinates) && isValidCoord(endLoc.coordinates)) {
                pathSegments.push({
                    from: startLoc.coordinates,
                    to: endLoc.coordinates,
                    options: {
                        color: '#3498db',
                        weight: 5,
                        opacity: 0.8,
                        dashArray: '10, 10' // This will make the line dashed
                    }
                });
            }
        }
    });
  });

  const handleStartMoving = (locationId) => {
    setIsMoving(locationId);
  };

  const handleMoveLocation = (locationId, newCoordinates) => {
    onLocationMove(locationId, newCoordinates);
    setIsMoving(null);
  };

  // Handle clicking on map during move mode
  const handleMapClick = (locationId, newCoordinates) => {
    onLocationMove(locationId, newCoordinates);
    setIsMoving(null);
  };

  if (!locations || locations.length === 0) {
    return <div className="map-container">No locations to display on map</div>;
  }

  return (
    <div className="map-container">
      <MapContainer 
        center={initialCenter} 
        zoom={13} 
        style={{ height: '100%', width: '100%' }}
        attributionControl={false}
      >
        <MapController locations={locations} />
        
        {useGoogleMaps ? (
          /* Map Styles: Google Maps raster tiles (same vt URL pattern as QuestApp web).
             In the dark theme the tiles are darkened via a CSS filter on the
             Leaflet tile pane (see index.css) — provider-agnostic, full detail kept. */
          <TileLayer
            attribution=""
            url="https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en&scale=2"
            subdomains={['0', '1', '2', '3']}
            maxZoom={20}
            tileSize={512}
            zoomOffset={-1}
          />
        ) : (
          /* Map Styles: Carto Voyager raster tiles — the same basemap QuestApp
             renders in gameplay, so the editor preview matches the played quest.
             We request @2x tiles directly (512px drawn into a 256px slot) for
             crisp text on HiDPI screens WITHOUT Leaflet's detectRetina, which
             bumps the zoom by one and renders every label at half size.
             In the dark theme these tiles are inverted via a CSS filter on the
             Leaflet tile pane (see index.css), same as the OSM/Google layers. */
          <TileLayer
            attribution=""
            url={`https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png?key=${CARTO_KEY}`}
            maxZoom={20}
            maxNativeZoom={20}
          />
        )}
        
        {/* Draw path between locations with arrows */}
        <ArrowPath segments={pathSegments} />
        
        {/* Map Click Handler for move mode */}
        <MapClickHandler 
          isMoving={isMoving} 
          onMapClick={handleMapClick} 
        />
        
        {locations.map((location, index) => {
          if (!isValidCoord(location.coordinates)) return null;
          const dimmed = !lensMatchesPoint(
            lens, location, tagAssigneesByLocation[location.uuid], currentUsername
          );
          return (
            <React.Fragment key={location.uuid || location.id || index}>
              <DraggableMarker
                position={location.coordinates}
                icon={createNumberedIcon(index + 1, !dimmed && location.id === selectedLocationId, dimmed)}
                onMove={handleMoveLocation}
                isMoving={isMoving}
                locationId={location.id}
                onLocationSelect={onLocationSelect}
                location={location}
                onStartMoving={handleStartMoving}
                isDimmed={dimmed}
              />
            </React.Fragment>
          );
        })}
      </MapContainer>
      {isMoving && (
        <div className="move-mode-overlay">
          <div className="move-mode-message">
            Move Mode: Click on map or drag marker to place it
            <button onClick={() => setIsMoving(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestMap; 